<template>
  <div>
    <v-card
      v-if="claims.length > 0"
      border
      flat
      tile
    >
      <v-card-text class="px-0 py-0">
        <v-card
          class="bb-1 bc-extra-light-gray"
          flat
          tile
        >
          <v-card-text class="py-3 fs-12">
            <v-row
              class="d-flex"
              dense
            >
              <div class="mt-2">
                <span class="fw-600">{{ $bct('Total funding amount') }}{{ ': ' }}</span>
                <span class="ms-2">{{ currency(familySubsidy.award_amount) }}</span>
              </div>
              <v-spacer />

              <v-tooltip location="bottom">
                <template #activator="{ props: activatorProps }">
                  <div class="mt-2">
                    <v-icon
                      v-bind="activatorProps"
                      class="d-inline pe-2"
                      icon="info"
                    />
                    <span class="fw-600"> {{ $bct('Remaining funding amount') }}{{ ': ' }} </span>
                    <span class="ms-2">
                      {{ currency(familySubsidy.award_amount - familySubsidy.approved_amount) }}
                    </span>
                  </div>
                </template>
                <span>
                  {{ $bct('Unapproved claims are not included in this amount.') }}
                </span>
              </v-tooltip>
              <v-spacer />

              <v-btn
                @click="$refs.createContinueClaimDialog.open()"
                color="primary"
                prepend-icon="add"
              >
                {{ $bct('New claim') }}
              </v-btn>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="bb-1 bc-extra-light-gray pa-0"
          flat
          tile
        >
          <v-card-text class="pa-3 pr-0">
            <v-row dense>
              <v-col cols="1">
                <!-- Lock column -->
              </v-col>
              <v-col class="fw-600 fs-14">
                {{ $bct('ID') }}
              </v-col>
              <v-col class="fw-600 fs-14 ta-center">
                {{ $bct('Requested') }}
              </v-col>
              <v-col class="fw-600 fs-14 ta-center">
                {{ $bct('Approved') }}
              </v-col>
              <v-col class="fw-600 fs-14 ta-center">
                {{ $bct('Status') }}
              </v-col>
              <v-col class="fw-600 fs-14 ta-center">
                {{ $bct('Created at') }}
              </v-col>
              <v-col class="fw-600 fs-14 ta-center">
                {{ $bct('Last updated') }}
              </v-col>
              <v-col cols="1">
                <!-- Action column -->
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          v-for="(claim, index) in claims"
          :key="index"
          class="bb-1 bc-extra-light-gray pa-0"
          flat
          tile
        >
          <v-card-text class="pa-3 pr-0">
            <v-row
              class="c-black fs-14 fw-500"
              dense
            >
              <v-col cols="1">
                <v-tooltip location="bottom">
                  <template #activator="{ props: tooltipProps }">
                    <v-icon
                      v-bind="tooltipProps"
                      size="24"
                    >
                      {{ claim.locked ? 'lock' : 'lock_open' }}
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $bct(
                        claim.locked
                          ? 'The claim is locked. You cannot make changes.'
                          : 'The claim is unlocked. You can make changes.',
                      )
                    }}
                  </span>
                </v-tooltip>
              </v-col>
              <v-col>
                <span>{{ claim.id.slice(0, 6).toUpperCase() }}</span>
              </v-col>
              <v-col class="ta-center">
                <span>{{ currency(claim.requested_amount) }}</span>
              </v-col>
              <v-col class="fs-14 fw-500 ta-center">
                <span v-if="claim.approved">{{ currency(claim.approved_amount) }}</span>
              </v-col>
              <v-col class="ta-center">
                <v-tooltip location="bottom">
                  <template #activator="{ props: tooltipProps }">
                    <span
                      v-bind="tooltipProps"
                      :class="getStatusClasses(claim)"
                      class="rounded b-1 px-3 py-1"
                    >
                      {{ $bct(getClaimStatus(claim)) }}
                    </span>
                  </template>
                  <span>
                    <LongDate
                      :date="claim.approved_at || claim.denied_at || claim.submitted_at"
                      short-month
                    />
                  </span>
                </v-tooltip>
              </v-col>
              <v-col class="ta-center">
                <LongDate
                  :date="claim.created_at"
                  short-month
                />
              </v-col>
              <v-col class="ta-center">
                <LongDate
                  :date="claim.updated_at"
                  short-month
                />
              </v-col>
              <v-col
                class="c-primary ta-right mr-2"
                cols="1"
              >
                <ActionMenu
                  @click:action:continue="$refs.createContinueClaimDialog.open(claim.id)"
                  @click:action:delete="deleteClaim(claim)"
                  @click:action:view="$refs.claimReviewDialog.open(claim.id)"
                  :items="getClaimActions(claim)"
                  button-icon="more_vert"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <NullState
      v-else
      @new="$refs.createContinueClaimDialog.open()"
      data-testid="claims-null-state"
      new-button-title="New claim"
    />

    <CreateContinueClaimDialog
      v-if="familySubsidyProgram?.claim_schema_id"
      @save="load()"
      ref="createContinueClaimDialog"
      :family-subsidy="familySubsidy"
      :schema-id="familySubsidyProgram.claim_schema_id"
    />

    <ClaimReviewDialog
      @change="load()"
      ref="claimReviewDialog"
      :family-subsidy="familySubsidy"
    />
  </div>
</template>

<script setup>
import ActionMenu from '@/shared/components/ActionMenu.vue';
import ClaimReviewDialog from '@/shared/components/claims/ClaimReviewDialog.vue';
import CreateContinueClaimDialog from '@/shared/components/claims/CreateContinueClaimDialog.vue';
import { currency } from '@/plugins/filters';
import LongDate from '@/shared/components/LongDate.vue';
import NullState from '@/shared/components/NullState.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const props = defineProps({
  familySubsidy: {
    type: Object,
    default: null,
  },
  familySubsidyProgram: {
    type: Object,
    default: null,
  },
});

const { api } = useApi();
const eventBus = useEventBus();
const $bct = inject('translator');

const claims = ref([]);
const processing = ref(true);

async function load() {
  const params = {
    family_subsidy_id: props.familySubsidy.id,
  };

  const resp = await api.parent.claim.index(params);
  if (resp?.status !== 200) return;

  claims.value = resp.data;
  processing.value = false;
}

async function deleteClaim(claim) {
  processing.value = true;
  const resp = await api.parent.claim.destroy(claim.id);
  processing.value = false;
  if (resp?.status !== 200) return;

  eventBus.chime('Claim deleted.');
  void load();
}

function getClaimActions(claim) {
  if (claim.locked) return [{ avatar: 'visibility', title: 'Review', event: 'view' }];
  if (claim.submitted) return [{ avatar: 'edit', title: 'Edit', event: 'view' }];

  return [
    { avatar: 'edit', title: 'Continue', event: 'continue' },
    { avatar: 'delete', title: 'Delete', event: 'delete' },
  ];
}

function getClaimStatus(claim) {
  if (claim.approved) return 'Approved';
  if (claim.denied) return 'Denied';
  if (claim.submitted) return 'Submitted';
  return 'Draft';
}

function getStatusClasses(claim) {
  if (claim.approved) return 'bc-primary c-primary';
  if (claim.denied) return 'bc-red c-red';
  if (claim.submitted) return 'bc-black c-black';
  return 'bc-gray c-gray';
}

onMounted(load);
</script>
